import { useState } from "react";
import {
  Calendar,
  ChevronRight,
  Globe,
  Info,
  Lock,
  MoreHorizontal,
  Shield,
  Users,
} from "lucide-react";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

function n(x) {
  return Number.isFinite(Number(x)) ? Number(x) : null;
}

function pickVisibilityLabel(club) {
  if (!club || typeof club !== "object") return null;
  const settings = club.settings && typeof club.settings === "object" ? club.settings : {};
  const raw =
    club.visibility ??
    settings.visibility ??
    club.join_policy ??
    settings.join_policy ??
    club.privacy ??
    null;
  if (raw == null || raw === "") return null;
  const s = String(raw).toLowerCase().replace(/_/g, " ");
  if (s.includes("private") || s === "invite") return "Private";
  if (s.includes("public")) return "Public";
  if (s.includes("unlisted")) return "Unlisted";
  return String(raw).replace(/_/g, " ");
}

function formatClubCreatedAt(club) {
  const raw = club?.created_at ?? club?.createdAt ?? club?.created ?? null;
  if (raw == null || raw === "") return null;
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

function pickRules(club) {
  if (!club || typeof club !== "object") return [];
  const settings = club.settings && typeof club.settings === "object" ? club.settings : {};
  if (Array.isArray(settings.rules) && settings.rules.length) {
    return settings.rules.filter(Boolean).map(String);
  }
  if (Array.isArray(club.rules) && club.rules.length) {
    return club.rules.filter(Boolean).map(String);
  }
  return [];
}

/**
 * Reddit-style community sidebar for ClubHome: join CTA + club facts in one card.
 * Visual language aligned with `ClubThreadSidebar` (gradient shell, compact meta).
 *
 * `railExtras` reserved for future: bookmarks, moderators, tags, related clubs, links.
 *
 * @param {Record<string, unknown>} [props.railExtras]
 */
export default function ClubInfoRail({
  club,
  slug,
  accent = "emerald",
  avatarUrl,
  clubName,
  tagline = "",
  counts = {},
  isOwner,
  isArchived,
  isActiveMember,
  isPendingMember,
  joining,
  leaving,
  archiving,
  membershipActionErr,
  onJoin,
  onLeave,
  onToggleArchive,
  onOpenAbout,
  railExtras: _railExtras,
}) {
  void _railExtras;
  const [menuOpen, setMenuOpen] = useState(false);

  const visibility = pickVisibilityLabel(club);
  const createdLabel = formatClubCreatedAt(club);
  const rules = pickRules(club);
  const rulesPreview = rules.slice(0, 6);

  const memberCount =
    n(counts?.memberCount) ??
    n(club?.member_count) ??
    n(club?.members_count) ??
    n(club?.members) ??
    null;

  const description =
    club && typeof club.description === "string" && club.description.trim()
      ? club.description.trim()
      : null;

  const accentPrimary =
    accent === "sky"
      ? "border-sky-400/30 bg-sky-500/15 text-sky-50 hover:bg-sky-500/22"
      : "border-emerald-400/30 bg-emerald-500/15 text-emerald-50 hover:bg-emerald-500/22";

  const accentOutline =
    accent === "sky"
      ? "border-sky-400/25 text-sky-100 hover:bg-sky-500/10"
      : "border-emerald-400/25 text-emerald-100 hover:bg-emerald-500/10";

  const VisibilityIcon = visibility && String(visibility).toLowerCase().includes("private") ? Lock : Globe;

  return (
    <aside
      className={cn(
        "rounded-2xl border border-white/12 bg-gradient-to-b from-white/[0.07] to-white/[0.02]",
        "backdrop-blur-md shadow-[0_18px_50px_rgba(0,0,0,0.45)] overflow-hidden ring-1 ring-white/[0.06]",
        "w-full min-w-0 max-w-full box-border"
      )}
      aria-label="Club information"
    >
      <div className="p-4 sm:p-5 border-b border-white/10">
        <div className="flex gap-3 min-w-0">
          <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl border border-white/12 bg-black/30 overflow-hidden shrink-0 grid place-items-center text-lg font-bold text-white/90">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="h-full w-full object-cover" draggable={false} />
            ) : (
              clubName.slice(0, 1).toUpperCase()
            )}
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <h2 className="text-base sm:text-lg font-semibold text-white tracking-tight truncate">{clubName}</h2>
            {slug ? (
              <p className="mt-0.5 text-[11px] text-gray-500 font-mono truncate">c/{slug}</p>
            ) : null}
          </div>
        </div>

        {description ? (
          <p className="mt-3 text-sm text-gray-400 leading-relaxed line-clamp-6 whitespace-pre-line">{description}</p>
        ) : tagline ? (
          <p className="mt-3 text-sm text-gray-400 leading-relaxed line-clamp-4">{tagline}</p>
        ) : (
          <p className="mt-3 text-sm text-gray-500 italic">No description yet.</p>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {visibility ? (
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-black/25 px-2.5 py-1 text-[11px] font-medium text-gray-300">
              <VisibilityIcon className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              {visibility}
            </span>
          ) : null}
          {memberCount != null ? (
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-black/25 px-2.5 py-1 text-[11px] font-medium text-gray-300 tabular-nums">
              <Users className="h-3.5 w-3.5 text-gray-400 shrink-0" aria-hidden />
              {memberCount.toLocaleString()} members
            </span>
          ) : null}
          {createdLabel ? (
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-black/25 px-2.5 py-1 text-[11px] font-medium text-gray-300">
              <Calendar className="h-3.5 w-3.5 text-gray-400 shrink-0" aria-hidden />
              Created {createdLabel}
            </span>
          ) : null}
        </div>

        {isOwner && isArchived ? (
          <div className="mt-3 rounded-lg border border-amber-400/25 bg-amber-500/10 px-3 py-2 text-[11px] font-medium text-amber-100">
            Deleted (only you can see this)
          </div>
        ) : null}

        {membershipActionErr ? (
          <div className="mt-3 rounded-lg border border-red-400/25 bg-red-500/10 px-3 py-2 text-xs text-red-100">
            {membershipActionErr}
          </div>
        ) : null}

        <div className="mt-4 space-y-2">
          {isActiveMember ? (
            <div className="flex gap-2">
              <span className="flex flex-1 min-w-0 items-center justify-center rounded-xl border border-white/12 bg-white/[0.08] px-4 py-2.5 text-sm font-semibold text-gray-100">
                Joined
              </span>
              <div className="relative shrink-0">
                <button
                  type="button"
                  className="flex h-[42px] w-11 items-center justify-center rounded-xl border border-white/12 bg-white/[0.06] text-gray-200 hover:bg-white/[0.1]"
                  aria-label="Member options"
                  onClick={() => setMenuOpen((v) => !v)}
                >
                  <MoreHorizontal className="h-5 w-5" />
                </button>
                {menuOpen ? (
                  <div
                    className="absolute right-0 z-30 mt-1 w-48 overflow-hidden rounded-xl border border-white/12 bg-[#0b1528] shadow-xl"
                    onMouseLeave={() => setMenuOpen(false)}
                  >
                    {isOwner ? (
                      <button
                        type="button"
                        disabled={archiving}
                        onClick={() => {
                          setMenuOpen(false);
                          onToggleArchive();
                        }}
                        className="w-full px-4 py-3 text-left text-sm text-gray-200 hover:bg-white/[0.06]"
                      >
                        {isArchived ? "Restore club" : "Delete club"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={leaving}
                        onClick={() => {
                          setMenuOpen(false);
                          onLeave();
                        }}
                        className="w-full px-4 py-3 text-left text-sm text-gray-200 hover:bg-white/[0.06]"
                      >
                        Leave club
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          ) : isPendingMember ? (
            <div className="rounded-xl border border-amber-400/25 bg-amber-500/10 px-4 py-2.5 text-center text-sm font-medium text-amber-100">
              Request pending
            </div>
          ) : (
            <button
              type="button"
              onClick={onJoin}
              disabled={joining || leaving}
              className={cn(
                "flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition",
                accentPrimary,
                joining ? "opacity-60 cursor-not-allowed" : ""
              )}
            >
              {joining ? "Joining…" : "Join"}
              <ChevronRight className="h-4 w-4 opacity-80 shrink-0" aria-hidden />
            </button>
          )}

          <button
            type="button"
            onClick={onOpenAbout}
            className={cn(
              "flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition",
              "border-white/12 bg-white/[0.04] text-gray-200 hover:bg-white/[0.08]",
              accentOutline
            )}
          >
            <Info className="h-4 w-4 opacity-70 shrink-0" aria-hidden />
            Rules and full about
          </button>
        </div>
      </div>

      {rulesPreview.length > 0 ? (
        <div className="px-4 sm:px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
            <Shield className="h-3.5 w-3.5" aria-hidden />
            Rules
          </div>
          <ol className="mt-3 space-y-2 text-sm text-gray-400 list-decimal list-inside marker:text-gray-600">
            {rulesPreview.map((r, i) => (
              <li key={`${i}-${r.slice(0, 20)}`} className="leading-relaxed pl-0.5">
                {r}
              </li>
            ))}
          </ol>
          {rules.length > rulesPreview.length ? (
            <button
              type="button"
              onClick={onOpenAbout}
              className="mt-3 text-xs font-medium text-sky-300/90 hover:text-sky-200"
            >
              View all rules
            </button>
          ) : null}
        </div>
      ) : null}

    </aside>
  );
}
